/**
 * ChainVault WP - Asset List Component
 *
 * Displays the list of crypto assets in the wallet.
 * Accepts a networkFilter prop to filter by network.
 *
 * @package ChainVault
 * @since   1.0.0
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import toast from 'react-hot-toast';
import { playSendSound, playErrorSound, resumeAudio } from '../utils/sounds';
import { sendAsset } from '../utils/sendAsset';

/* Map asset symbols to their real CoinGecko coin IDs, so tapping an asset
   opens the correct detail page. Custom/unlisted tokens (no CoinGecko listing)
   are intentionally left out — tapping those won't navigate. */
const SYMBOL_TO_COINGECKO_ID = {
    'BTC':   'bitcoin',
    'ETH':   'ethereum',
    'SOL':   'solana',
    'BNB':   'binancecoin',
    'MATIC': 'matic-network',
    'USDT':  'tether',
    'USDC':  'usd-coin',
    'BUSD':  'binance-usd',
    'WBTC':  'wrapped-bitcoin',
    'BTCB':  'binance-bitcoin',
};

/* Stablecoins are always ~$1 — the app's `prices` object only fetches
   native-coin prices (ETH/BNB/MATIC/SOL/BTC), not stablecoins, so we
   short-circuit these rather than looking them up. */
const STABLECOIN_SYMBOLS = [ 'USDT', 'USDC', 'BUSD', 'DAI' ];

/* Native coins can never be hidden — hiding a wallet's main balance
   for a network would be confusing and easy to do by accident. */
const NATIVE_ASSET_IDS = [ 'ethereum', 'bnb', 'matic', 'base', 'solana', 'bitcoin' ];

/* Builds a stable identifier for an asset so it can be hidden/shown
   consistently regardless of which array (evmAssets, splAssets, customTokens)
   it originally came from. */
function getAssetUniqueId( asset ) {
    const key = ( asset.contractAddress || asset.mint || asset.address || asset.symbol || '' ).toLowerCase();
    return `${ asset.network }:${ key }`;
}

/**
 * Single Asset Row Component
 */
function AssetRow( { asset } ) {

    const {
        walletType,
        builtInWallet,
        sendFromBuiltIn,
        prices,
    } = useWallet();

    const navigate = useNavigate();
    const coinGeckoId = SYMBOL_TO_COINGECKO_ID[ ( asset.symbol || '' ).toUpperCase() ];

    const handleRowClick = () => {
        if ( coinGeckoId ) {
            navigate( `/wallet/asset/${ coinGeckoId }`, {
                state: {
                    id:      asset.id,
                    symbol:  asset.symbol,
                    name:    asset.name,
                    balance: asset.balance,
                    network: asset.network,
                    icon:    asset.icon,
                    address: asset.address || asset.mint || asset.contractAddress,
                    mint:    asset.mint,
                    contractAddress: asset.contractAddress,
                    decimals: asset.decimals,
                },
            } );
        }
    };

    const [ showActions,   setShowActions   ] = useState( false );
    const [ menuPos,       setMenuPos       ] = useState( { top: 0 } );
    const [ showSendModal, setShowSendModal ] = useState( false );
    const [ showReceive,   setShowReceive   ] = useState( false );
    const [ sendTo,        setSendTo        ] = useState( '' );
    const [ sendAmount,    setSendAmount    ] = useState( '' );
    const [ isSending,     setIsSending     ] = useState( false );
    const [ iconError,     setIconError     ] = useState( false );

    const getAssetAddress = () => {
        if ( walletType === 'builtin' && builtInWallet ) {
            if ( asset.network === 'solana' )  return builtInWallet.solana.address;
            if ( asset.network === 'bitcoin' ) return builtInWallet.bitcoin.address;
            return builtInWallet.ethereum.address;
        }
        return asset.address || '';
    };

    const assetAddress = getAssetAddress();

    const copyAddress = () => {
        if ( assetAddress ) {
            navigator.clipboard.writeText( assetAddress );
            toast.success( `${ asset.symbol } address copied!` );
        } else {
            toast.error( 'No address available.' );
        }
        setShowActions( false );
    };

    const handleSend = async () => {
        setIsSending( true );
        try {
            const result = await sendAsset( { asset, sendTo, sendAmount, walletType, builtInWallet, sendFromBuiltIn } );
            await resumeAudio();
            playSendSound();
            toast.success( result.hash ? `Sent ${ sendAmount } ${ asset.symbol }! ${ result.hash.slice(0,16) }...` : 'Transaction submitted!' );
            setShowSendModal( false );
            setSendTo( '' );
            setSendAmount( '' );
        } catch ( err ) {
            console.error( err );
            playErrorSound();
            toast.error( err.message || 'Transaction failed.' );
        } finally {
            setIsSending( false );
        }
    };

    const isMobile = window.innerWidth <= 768;

    const priceUSD = ( () => {
        const symUpper = ( asset.symbol || '' ).toUpperCase();
        if ( STABLECOIN_SYMBOLS.includes( symUpper ) ) return 1;
        if ( symUpper === 'WBTC' || symUpper === 'BTCB' ) return prices?.[ 'bitcoin' ]?.usd || 0;
        const cgId = SYMBOL_TO_COINGECKO_ID[ symUpper ];
        return cgId ? ( prices?.[ cgId ]?.usd || 0 ) : 0;
    } )();
    const usdVal = '$' + ( parseFloat( asset.balance || '0' ) * priceUSD ).toFixed( 2 );



    return (
        <>
            <div
                className="cv-asset-row"
                onClick={ handleRowClick }
                style={ {
                    cursor: coinGeckoId ? 'pointer' : 'default',
                    ...( isMobile ? {
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '12px 14px',
                        background: 'rgba(255,255,255,0.03)',
                        borderRadius: '14px',
                        marginBottom: '8px',
                        border: '1px solid rgba(255,255,255,0.06)',
                    } : {} )
                } }
            >

                <div className="cv-asset-icon-wrap">
                    { asset.icon && ! iconError ? (
                        <img
                            src={ asset.icon }
                            alt={ asset.name }
                            className="cv-asset-icon"
                            style={ isMobile ? { width: '42px', height: '42px', borderRadius: '50%' } : {} }
                            onError={ () => setIconError( true ) }
                        />
                    ) : (
                        <div
                            className="cv-asset-icon-placeholder-filled"
                            style={{ background: asset.color || '#6C63FF',
                                ...(isMobile ? { width: '42px', height: '42px', fontSize: '16px' } : {})
                            }}
                        >
                            { asset.symbol.charAt( 0 ) }
                        </div>
                    ) }
                </div>

                <div className="cv-asset-info" style={{ flex: 1 }}>
                    <span className="cv-asset-name">{ asset.name }</span>
                    { isMobile ? (
                        <span style={{ fontSize: '12px', color: '#666' }}>
                            { parseFloat( asset.balance || '0' ).toFixed( 6 ) } { asset.symbol }
                        </span>
                    ) : (
                        <button className="cv-show-address-btn" onClick={ (e) => { e.stopPropagation(); copyAddress(); } }>
                            { assetAddress
                                ? `${ assetAddress.slice( 0, 8 ) }...${ assetAddress.slice( -4 ) }`
                                : 'Show address'
                            }
                        </button>
                    )}
                </div>

                <div className="cv-asset-balance" style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: '700', color: '#fff' }}>
                        { parseFloat( asset.balance || '0' ).toFixed( isMobile ? 4 : 6 ) } { asset.symbol }
                    </div>
                    <div style={{ fontSize: '11px', color: '#fff' }}>
                        { usdVal }
                    </div>
                </div>

                <button
                    className="cv-asset-menu-btn"
                    onClick={ (e) => { e.stopPropagation(); setShowActions( ! showActions ); } }
                >
                    ⋮
                </button>

                { showActions && (
                    <>
                        <div
                            style={{ position:'fixed', inset:0, zIndex: 49, background: 'rgba(0,0,0,0.01)', cursor: 'default' }}
                            onClick={ (e) => { e.stopPropagation(); setShowActions( false ); } }
                        />
                        <div className="cv-asset-actions-dropdown" style={{ zIndex: 999 }}>
                            <button onClick={ copyAddress }>
                                📋 Copy Address
                            </button>
                            <button onClick={ () => {
                                setShowActions( false );
                                setShowSendModal( true );
                            } }>
                                📤 Send
                            </button>
                            <button onClick={ () => {
                                setShowActions( false );
                                setShowReceive( true );
                            } }>
                                📥 Receive
                            </button>
                            { ! NATIVE_ASSET_IDS.includes( asset.id ) && (
                                <button onClick={ (e) => {
                                    e.stopPropagation();
                                    setShowActions( false );
                                    const hidden = JSON.parse( localStorage.getItem( 'chainvault_hidden_assets' ) || '[]' );
                                    const uniqueId = getAssetUniqueId( asset );
                                    if ( ! hidden.some( h => h.id === uniqueId ) ) {
                                        hidden.push( { id: uniqueId, symbol: asset.symbol, name: asset.name, icon: asset.icon, network: asset.network } );
                                        localStorage.setItem( 'chainvault_hidden_assets', JSON.stringify( hidden ) );
                                    }
                                    window.dispatchEvent( new Event( 'chainvault_tokens_updated' ) );
                                    toast.success( asset.symbol + ' hidden — you can unhide it in Settings > Manage Assets' );
                                } }>
                                    🙈 Hide Asset
                                </button>
                            ) }
                            { asset.address && !['ethereum','bsc','polygon','base','solana','bitcoin'].includes( asset.id ) && (
                                <button onClick={ (e) => {
                                    e.stopPropagation();
                                    setShowActions( false );
                                    /* Remove from localStorage custom tokens */
                                    const stored = JSON.parse( localStorage.getItem( 'chainvault_custom_tokens' ) || '[]' );
                                    const updated = stored.filter( t => t.address.toLowerCase() !== asset.address.toLowerCase() );
                                    localStorage.setItem( 'chainvault_custom_tokens', JSON.stringify( updated ) );
                                    /* Also remove from hidden solTokens via event */
                                    const hidden = JSON.parse( localStorage.getItem( 'chainvault_hidden_spl' ) || '[]' );
                                    if ( !hidden.includes( asset.address ) ) hidden.push( asset.address );
                                    localStorage.setItem( 'chainvault_hidden_spl', JSON.stringify( hidden ) );
                                    window.dispatchEvent( new Event( 'chainvault_tokens_updated' ) );
                                    toast.success( asset.symbol + ' removed from wallet' );
                                } } style={{ color: '#ff4444' }}>
                                    🗑️ Remove Token
                                </button>
                            ) }
                        </div>
                    </>
                ) }

            </div>

            { /* Send Modal */ }
            { showSendModal && (
                <div
                    className="cv-modal-overlay"
                    onClick={ () => setShowSendModal( false ) }
                >
                    <div
                        className="cv-modal"
                        onClick={ e => e.stopPropagation() }
                    >
                        <div className="cv-modal-header">
                            <h3>Send { asset.symbol }</h3>
                            <button
                                className="cv-modal-close"
                                onClick={ () => setShowSendModal( false ) }
                            >✕</button>
                        </div>
                        <div className="cv-send-content">
                            <div className="cv-field-group">
                                <label>Recipient Address</label>
                                <input
                                    type="text"
                                    className="cv-input"
                                    placeholder={ asset.network === 'solana' ? 'Solana address...' : '0x...' }
                                    value={ sendTo }
                                    onChange={ e => setSendTo( e.target.value ) }
                                />
                            </div>
                            <div className="cv-field-group">
                                <label>Amount ({ asset.symbol })</label>
                                <input
                                    type="number"
                                    className="cv-input"
                                    placeholder="0.00"
                                    value={ sendAmount }
                                    onChange={ e => setSendAmount( e.target.value ) }
                                />
                                <span style={{ fontSize: '12px', color: '#888' }}>
                                    Balance: { parseFloat( asset.balance || '0' ).toFixed( 6 ) } { asset.symbol }
                                </span>
                            </div>
                            <button
                                className="cv-btn-primary full-width"
                                onClick={ handleSend }
                                disabled={ isSending }
                            >
                                { isSending ? 'Sending...' : `Send ${ asset.symbol }` }
                            </button>
                        </div>
                    </div>
                </div>
            ) }

            { /* Receive Modal */ }
            { showReceive && (
                <div
                    className="cv-modal-overlay"
                    onClick={ () => setShowReceive( false ) }
                >
                    <div
                        className="cv-modal"
                        onClick={ e => e.stopPropagation() }
                    >
                        <div className="cv-modal-header">
                            <h3>Receive { asset.symbol }</h3>
                            <button
                                className="cv-modal-close"
                                onClick={ () => setShowReceive( false ) }
                            >✕</button>
                        </div>
                        <div className="cv-deposit-content">
                            <p style={{ color: '#888', fontSize: '14px', marginBottom: '16px' }}>
                                Send { asset.symbol } to this address:
                            </p>
                            { assetAddress ? (
                                <>
                                    <div className="cv-address-display">
                                        <code>{ assetAddress }</code>
                                        <button
                                            className="cv-copy-btn"
                                            onClick={ copyAddress }
                                        >
                                            Copy
                                        </button>
                                    </div>
                                    <p style={{ color: '#888', fontSize: '12px', marginTop: '12px' }}>
                                        Only send { asset.symbol } to this address.
                                        Sending other tokens may result in permanent loss.
                                    </p>
                                </>
                            ) : (
                                <p style={{ color: '#888' }}>No address available.</p>
                            ) }
                        </div>
                    </div>
                </div>
            ) }
        </>
    );
}

/**
 * AssetList Component
 */
function AssetList( { networkFilter = 'all', tokens: customTokensProp = [] } ) {

    const {
        balances,
        tokens,
        solTokens,
        walletType,
        builtInWallet,
        selectedNetwork,
    } = useWallet();

    const isTestnet = localStorage.getItem( 'chainvault_testnet_mode' ) === '1';
    const ethAddr   = walletType === 'builtin' && builtInWallet ? builtInWallet.ethereum.address : '';
    const [ erc20Balances, setErc20Balances ] = React.useState( {} );

    /* Fetch real ERC20 token balances for the connected wallet — extracted as a
       reusable callback so the per-row refresh button can trigger it on demand too. */
    const fetchERC20Balances = React.useCallback( async () => {
        if ( ! ethAddr || isTestnet ) return;
            try {
                const { ethers } = await import( 'ethers' );
                const rpcMap = { ethereum: 'https://ethereum-rpc.publicnode.com', bsc: 'https://bsc-dataseed.binance.org', polygon: 'https://polygon-bor-rpc.publicnode.com', base: 'https://mainnet.base.org' };
                const abi = [ 'function balanceOf(address) view returns (uint256)', 'function decimals() view returns (uint8)' ];
                const newBalances = {};
                /* Fetch balances for all ERC20 tokens across all networks */
                const tokenList = [
                    { id: 'ETH_USDT',  address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', network: 'ethereum', decimals: 6  },
                    { id: 'ETH_USDC',  address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', network: 'ethereum', decimals: 6  },
                    { id: 'ETH_WBTC',  address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', network: 'ethereum', decimals: 8  },
                    { id: 'BSC_USDT',  address: '0x55d398326f99059fF775485246999027B3197955', network: 'bsc',      decimals: 18 },
                    { id: 'BSC_BUSD',  address: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56', network: 'bsc',      decimals: 18 },
                    { id: 'POL_USDT',  address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', network: 'polygon',  decimals: 6  },
                    { id: 'POL_USDC',  address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174', network: 'polygon',  decimals: 6  },
                    { id: 'BASE_USDC', address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', network: 'base',     decimals: 6  },
                ];
                await Promise.all( tokenList.map( async ( tok ) => {
                    try {
                        const provider = new ethers.JsonRpcProvider( rpcMap[ tok.network ] );
                        const contract = new ethers.Contract( tok.address, abi, provider );
                        const bal = await contract.balanceOf( ethAddr );
                        newBalances[ tok.id ] = parseFloat( ethers.formatUnits( bal, tok.decimals ) ).toFixed( 6 );
                    } catch(e) { newBalances[ tok.id ] = '0'; }
                }) );
                setErc20Balances( newBalances );
            } catch(e) { console.error( 'ERC20 balance fetch error:', e ); }
    }, [ ethAddr, isTestnet ] ); // eslint-disable-line

    React.useEffect( () => {
        fetchERC20Balances();
        const interval = setInterval( fetchERC20Balances, 30000 ); /* refresh every 30s */
        return () => clearInterval( interval );
    }, [ fetchERC20Balances ] );

    /* Mainnet token contract addresses */
    const TOKENS = {
        /* Ethereum */
        ETH_WBTC:  '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599',
        ETH_USDT:  '0xdAC17F958D2ee523a2206206994597C13D831ec7',
        ETH_USDC:  '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
        /* BSC */
        BSC_BTCB:  '0x7130d2A12B9BCbFAe4f2634d864A1Ee1Ce3Ead9c',
        BSC_USDT:  '0x55d398326f99059fF775485246999027B3197955',
        BSC_BUSD:  '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56',
        /* Polygon */
        POL_WBTC:  '0x1BFD67037B42Cf73acF2047067bd4F2C47D9BfD6',
        POL_USDT:  '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
        POL_USDC:  '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
        /* Base */
        BASE_WBTC: '0xd9aAEc86B65D86f6A7B5B1b0c42FFA531710b6CA',
        BASE_USDC: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
    };

    const evmAssets = [
        /* Ethereum */
        { id: 'ethereum', name: 'Ethereum',    symbol: 'ETH',  balance: balances.ethereum || '0', color: '#627EEA', icon: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png',  network: 'ethereum', address: ethAddr },
        { id: TOKENS.ETH_WBTC, name: 'Wrapped Bitcoin', symbol: 'WBTC', balance: erc20Balances.ETH_WBTC || '0', color: '#F7931A', icon: 'https://assets.coingecko.com/coins/images/7598/small/wrapped_bitcoin_wbtc.png', network: 'ethereum', address: ethAddr, contractAddress: TOKENS.ETH_WBTC },
        { id: TOKENS.ETH_USDT, name: 'Tether USD',      symbol: 'USDT', balance: erc20Balances.ETH_USDT || '0', color: '#26A17B', icon: 'https://assets.coingecko.com/coins/images/325/small/Tether.png',               network: 'ethereum', address: ethAddr, contractAddress: TOKENS.ETH_USDT },
        { id: TOKENS.ETH_USDC, name: 'USD Coin',        symbol: 'USDC', balance: erc20Balances.ETH_USDC || '0', color: '#2775CA', icon: 'https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png',        network: 'ethereum', address: ethAddr, contractAddress: TOKENS.ETH_USDC },
        /* BSC */
        { id: 'bnb',  name: 'BNB',             symbol: 'BNB',  balance: balances.bsc     || '0', color: '#F0B90B', icon: 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png', network: 'bsc', address: ethAddr },
        { id: TOKENS.BSC_BTCB, name: 'Bitcoin BEP20',   symbol: 'BTCB', balance: '0', color: '#F7931A', icon: 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png',                 network: 'bsc', address: ethAddr, contractAddress: TOKENS.BSC_BTCB },
        { id: TOKENS.BSC_USDT, name: 'Tether USD',      symbol: 'USDT', balance: erc20Balances.BSC_USDT || '0', color: '#26A17B', icon: 'https://assets.coingecko.com/coins/images/325/small/Tether.png',               network: 'bsc', address: ethAddr, contractAddress: TOKENS.BSC_USDT },
        { id: TOKENS.BSC_BUSD, name: 'Binance USD',     symbol: 'BUSD', balance: erc20Balances.BSC_BUSD || '0', color: '#F0B90B', icon: 'https://assets.coingecko.com/coins/images/9576/small/BUSD.png',                 network: 'bsc', address: ethAddr, contractAddress: TOKENS.BSC_BUSD },
        /* Polygon */
        { id: 'matic', name: 'Polygon',        symbol: 'MATIC', balance: balances.polygon || '0', color: '#8247E5', icon: 'https://assets.coingecko.com/coins/images/4713/small/polygon.png', network: 'polygon', address: ethAddr },
        { id: TOKENS.POL_WBTC, name: 'Wrapped Bitcoin', symbol: 'WBTC', balance: '0', color: '#F7931A', icon: 'https://assets.coingecko.com/coins/images/7598/small/wrapped_bitcoin_wbtc.png', network: 'polygon', address: ethAddr, contractAddress: TOKENS.POL_WBTC },
        { id: TOKENS.POL_USDT, name: 'Tether USD',      symbol: 'USDT', balance: erc20Balances.POL_USDT || '0', color: '#26A17B', icon: 'https://assets.coingecko.com/coins/images/325/small/Tether.png',               network: 'polygon', address: ethAddr, contractAddress: TOKENS.POL_USDT },
        { id: TOKENS.POL_USDC, name: 'USD Coin',        symbol: 'USDC', balance: erc20Balances.POL_USDC || '0', color: '#2775CA', icon: 'https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png',        network: 'polygon', address: ethAddr, contractAddress: TOKENS.POL_USDC },
        /* Base */
        { id: 'base',  name: 'Base ETH',       symbol: 'ETH',  balance: balances.base    || '0', color: '#0052FF', icon: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png',  network: 'base', address: ethAddr },
        { id: TOKENS.BASE_WBTC, name: 'Wrapped Bitcoin', symbol: 'WBTC', balance: '0', color: '#F7931A', icon: 'https://assets.coingecko.com/coins/images/7598/small/wrapped_bitcoin_wbtc.png', network: 'base', address: ethAddr, contractAddress: TOKENS.BASE_WBTC },
        { id: TOKENS.BASE_USDC, name: 'USD Coin',        symbol: 'USDC', balance: erc20Balances.BASE_USDC || '0', color: '#2775CA', icon: 'https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png',        network: 'base', address: ethAddr, contractAddress: TOKENS.BASE_USDC },
    ];

    const SOL_USDT_MINT = 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB';
    const SOL_USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';
    const solAddr = walletType === 'builtin' && builtInWallet ? builtInWallet.solana.address : '';

    /* Note: USDT/USDC on Solana are NOT hardcoded here — they come from the real
       live-fetched splAssets below (via solTokens from WalletContext), which reflects
       actual on-chain balances. Hardcoding them here caused duplicate "Tether USD" /
       "USD Coin" rows (always $0) alongside the real ones. */
    const solanaAssets = [
        { id: 'solana', name: 'Solana', symbol: 'SOL', balance: balances.solana || '0', color: '#9945FF', icon: 'https://assets.coingecko.com/coins/images/4128/small/solana.png', network: 'solana', address: solAddr },
    ];

    const hiddenSpl = JSON.parse( localStorage.getItem( 'chainvault_hidden_spl' ) || '[]' );
    const splAssets = ( solTokens || [] ).filter( token => !hiddenSpl.includes( token.mint ) ).map( token => ( {
        id:      token.mint,
        name:    token.name,
        symbol:  token.symbol,
        balance: token.balance,
        color:   token.color || '#6C63FF',
        icon:    token.icon  || null,
        network: 'solana',
        address: token.mint,
    } ) );

    const allCustomTokens = [ ...tokens, ...customTokensProp ];

    /* Deduplicate — remove custom tokens whose contract address already
     * exists in the built-in evmAssets list, to prevent duplicates when
     * a user has manually added a token that is also in the default list. */
    const builtInAddresses = new Set(
        evmAssets
            .filter( a => a.contractAddress )
            .map( a => a.contractAddress.toLowerCase() )
    );

    const customTokens = allCustomTokens
        .filter( token => ! builtInAddresses.has( ( token.address || '' ).toLowerCase() ) )
        .map( token => ( {
            id:      token.address,
            name:    token.name,
            symbol:  token.symbol,
            balance: token.balance || '0',
            color:   '#6C63FF',
            icon:    token.icon  || null,
            network: token.network || 'ethereum',
            address: token.address,
        } ) );

    let allAssets = [];

    /* Always show all assets regardless of selected network.
     * The network selector controls send/receive — not asset visibility. */
    if ( walletType === 'builtin' ) {
        allAssets = [ ...evmAssets, ...solanaAssets, ...splAssets, ...customTokens ];
    } else if ( walletType === 'phantom' ) {
        allAssets = [ ...solanaAssets, ...splAssets ];
    } else {
        allAssets = [ ...evmAssets, ...customTokens ];
    }

    /* Filter out user-hidden assets (native coins are never hideable) */
    const hiddenAssetIds = new Set(
        JSON.parse( localStorage.getItem( 'chainvault_hidden_assets' ) || '[]' ).map( h => h.id )
    );
    allAssets = allAssets.filter( asset =>
        NATIVE_ASSET_IDS.includes( asset.id ) || ! hiddenAssetIds.has( getAssetUniqueId( asset ) )
    );

    /* Sort so the active network's native token always appears first */
    const sortedAssets = [ ...allAssets ].sort( ( a, b ) => {
        if ( a.network === selectedNetwork && b.network !== selectedNetwork ) return -1;
        if ( b.network === selectedNetwork && a.network !== selectedNetwork ) return 1;
        return 0;
    } );

    const assetsToShow = networkFilter === 'all'
        ? sortedAssets
        : sortedAssets.filter( asset => asset.network === networkFilter );

    return (
        <div className="cv-asset-list">
            { assetsToShow.length === 0 ? (
                <div style={{ padding: '20px', color: '#888', fontSize: '14px', textAlign: 'center' }}>
                    No assets found for this network.
                </div>
            ) : (
                assetsToShow.map( asset => (
                    <AssetRow
                        key={ asset.id }
                        asset={ asset }
                    />
                ) )
            ) }
        </div>
    );
}

export default AssetList;